/**
 * 💰 Balans Service
 * 
 * İstifadəçi balansı və depozit əməliyyatları üçün təhlükəsiz servis.
 * Real-time yenilənmə dəstəyi ilə.
 * 
 * @version 1.0.0
 */

import axios from 'axios';

const API_BASE_URL = 'https://api.herracim.az';

// Axios instance with credentials
const api = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Error handler
const handleError = (error, defaultMessage) => {
    console.error(defaultMessage, error);
    
    if (error.response) {
        return {
            success: false,
            error: error.response.data?.message || defaultMessage,
            statusCode: error.response.status,
        };
    }
    
    return {
        success: false,
        error: error.message || defaultMessage,
        statusCode: null,
    };
};

// ═══════════════════════════════════════════════════════════════
// 💰 BALANS ƏMƏLİYYATLARI
// ═══════════════════════════════════════════════════════════════

/**
 * İstifadəçi balansını alır
 * @param {string} userId - İstifadəçi ID
 * @returns {Promise<Object>}
 */
export const getBalance = async (userId) => {
    if (!userId) {
        return { success: false, error: 'İstifadəçi ID tələb olunur' };
    }

    try {
        const response = await api.get(`/api/BidDeposit/GetBalance/${userId}`);
        
        return {
            success: true,
            data: {
                balance: Number(response.data.balance) || 0,
                activeDeposits: Number(response.data.activeDeposits) || 0,
                availableBalance: Number(response.data.availableBalance) || 0,
                totalAssets: Number(response.data.totalAssets) || 0,
            },
        };
    } catch (error) {
        return handleError(error, 'Balans alınmadı');
    }
};

/**
 * Balans artırır (Admin/Test üçün)
 * @param {string} userId - İstifadəçi ID
 * @param {number} amount - Məbləğ
 * @returns {Promise<Object>}
 */
export const addBalance = async (userId, amount) => {
    if (!userId || !amount || amount <= 0) {
        return { success: false, error: 'Düzgün parametrlər daxil edin' };
    }

    try {
        const response = await api.post('/api/BidDeposit/AddBalance', null, {
            params: { userId, amount },
        });
        
        return {
            success: true,
            data: {
                message: response.data.message,
                newBalance: Number(response.data.balance) || 0,
            },
        };
    } catch (error) {
        return handleError(error, 'Balans artırıla bilmədi');
    }
};

// ═══════════════════════════════════════════════════════════════
// 📊 DEPOZİT ƏMƏLİYYATLARI
// ═══════════════════════════════════════════════════════════════

/**
 * İstifadəçinin depozitlərini alır
 * @param {string} userId - İstifadəçi ID
 * @returns {Promise<Object>}
 */
export const getUserDeposits = async (userId) => {
    if (!userId) {
        return { success: false, error: 'İstifadəçi ID tələb olunur' };
    }

    try {
        const response = await api.get(`/api/BidDeposit/GetUserDeposits/${userId}`);
        
        return {
            success: true,
            data: {
                deposits: response.data.deposits || [],
                totalDeposits: response.data.totalDeposits || 0,
                activeDeposits: response.data.activeDeposits || 0,
                refundedDeposits: response.data.refundedDeposits || 0,
                totalDepositAmount: Number(response.data.totalDepositAmount) || 0,
            },
        };
    } catch (error) {
        return handleError(error, 'Depozitlər alınmadı');
    }
};

/**
 * Məhsul üçün depozit statusunu yoxlayır
 * @param {number} productId - Məhsul ID
 * @returns {Promise<Object>}
 */
export const hasDeposit = async (productId) => {
    try {
        const response = await api.get('/api/BidDeposit/HasDeposit', {
            params: { productId },
        });
        
        return {
            success: true,
            hasDeposit: response.data.hasDeposit === true,
        };
    } catch (error) {
        return { success: false, hasDeposit: false };
    }
};

/**
 * Qalib olub-olmadığını yoxlayır
 * @param {number} productId - Məhsul ID
 * @returns {Promise<Object>}
 */
export const isWinner = async (productId) => {
    try {
        const response = await api.get('/api/BidDeposit/IsWinner', {
            params: { productId },
        });
        
        return {
            success: true,
            isWinner: response.data.isWinner === true,
            message: response.data.message,
        };
    } catch (error) {
        return { success: false, isWinner: false };
    }
};

/**
 * Depozit təsdiqləyib-təsdiqləmədiyini yoxlayır
 * @param {number} productId - Məhsul ID
 * @returns {Promise<Object>}
 */
export const hasConfirmed = async (productId) => {
    try {
        const response = await api.get('/api/BidDeposit/HasConfirmed', {
            params: { productId },
        });
        
        return {
            success: true,
            confirmed: response.data.confirmed === true,
        };
    } catch (error) {
        return { success: false, confirmed: false };
    }
};

/**
 * Depoziti təsdiqləyir (Qalib üçün)
 * @param {number} productId - Məhsul ID
 * @returns {Promise<Object>}
 */
export const confirmDeposit = async (productId) => {
    try {
        const response = await api.post('/api/BidDeposit/ConfirmDeposit', {
            productId,
        });
        
        return {
            success: true,
            data: {
                message: response.data.message,
                depositAmount: Number(response.data.depositAmount) || 0,
                platformFee: Number(response.data.platformFee) || 0,
                sellerGets: Number(response.data.sellerGets) || 0,
                highestBid: Number(response.data.highestBid) || 0,
            },
        };
    } catch (error) {
        return handleError(error, 'Depozit təsdiqlənə bilmədi');
    }
};

// ═══════════════════════════════════════════════════════════════
// 🚨 ŞİKAYƏT SİSTEMİ
// ═══════════════════════════════════════════════════════════════

/**
 * Şikayət göndərir
 * @param {number} productId - Məhsul ID
 * @param {string} message - Şikayət mətni
 * @returns {Promise<Object>}
 */
export const submitReport = async (productId, message) => {
    if (!productId || !message?.trim()) {
        return { success: false, error: 'Məhsul ID və şikayət mətni tələb olunur' };
    }

    try {
        const response = await api.post('/api/BidDeposit/SubmitReport', {
            productId,
            message: message.trim(),
        });
        
        return {
            success: true,
            data: {
                message: response.data.message,
                reportId: response.data.reportId,
            },
        };
    } catch (error) {
        return handleError(error, 'Şikayət göndərilə bilmədi');
    }
};

/**
 * Şikayət edib-etmədiyini yoxlayır
 * @param {number} productId - Məhsul ID
 * @returns {Promise<Object>}
 */
export const hasReported = async (productId) => {
    try {
        const response = await api.get('/api/BidDeposit/HasReported', {
            params: { productId },
        });
        
        return {
            success: true,
            hasReported: response.data.hasReported === true,
        };
    } catch (error) {
        return { success: false, hasReported: false };
    }
};

// ═══════════════════════════════════════════════════════════════
// 📊 MƏLUMAT SERVİSLƏRİ
// ═══════════════════════════════════════════════════════════════

/**
 * Məhsul üçün depozit məlumatlarını alır
 * @param {number} productId - Məhsul ID
 * @returns {Promise<Object>}
 */
export const getDepositInfo = async (productId) => {
    try {
        const response = await api.get(`/api/BidDeposit/GetDepositInfo/${productId}`);
        
        return {
            success: true,
            data: {
                productId: response.data.productId,
                productPrice: Number(response.data.productPrice) || 0,
                currentHighestBid: Number(response.data.currentHighestBid) || 0,
                depositAmount: Number(response.data.depositAmount) || 0,
                platformFee: Number(response.data.platformFee) || 0,
                sellerGets: Number(response.data.sellerGets) || 0,
                nextMinBid: Number(response.data.nextMinBid) || 0,
                minBidIncrement: Number(response.data.minBidIncrement) || 0,
                maxDeposit: Number(response.data.maxDeposit) || 1000,
                platformFeePercentage: Number(response.data.platformFeePercentage) || 1,
            },
        };
    } catch (error) {
        return handleError(error, 'Depozit məlumatı alınmadı');
    }
};

/**
 * Depozit cədvəlini alır
 * @returns {Promise<Object>}
 */
export const getDepositTable = async () => {
    try {
        const response = await api.get('/api/BidDeposit/GetDepositTable');
        
        return {
            success: true,
            data: {
                table: response.data.table || [],
                maxDeposit: Number(response.data.maxDeposit) || 1000,
                platformFeePercentage: Number(response.data.platformFeePercentage) || 1,
                maxFeeFromDeposit: Number(response.data.maxFeeFromDeposit) || 90,
            },
        };
    } catch (error) {
        return handleError(error, 'Depozit cədvəli alınmadı');
    }
};

// ═══════════════════════════════════════════════════════════════
// 🔄 REAL-TIME YENİLƏMƏ
// ═══════════════════════════════════════════════════════════════

/**
 * Balans polling funksiyası
 * @param {string} userId - İstifadəçi ID
 * @param {Function} onUpdate - Yenilənmə callback
 * @param {number} interval - Yeniləmə intervalı (ms)
 * @returns {Function} - Stop funksiyası
 */
export const startBalancePolling = (userId, onUpdate, interval = 30000) => {
    if (!userId || !onUpdate) {
        console.warn('Balance polling: userId və onUpdate tələb olunur');
        return () => {};
    }

    const fetchAndUpdate = async () => {
        const result = await getBalance(userId);
        if (result.success) {
            onUpdate(result.data);
        }
    };

    // İlkin yükləmə
    fetchAndUpdate();

    // Periodic polling
    const intervalId = setInterval(fetchAndUpdate, interval);

    // Stop funksiyası
    return () => {
        clearInterval(intervalId);
    };
};

// Default export
const BalanceService = {
    // Balance
    getBalance,
    addBalance,
    
    // Deposits
    getUserDeposits,
    hasDeposit,
    isWinner,
    hasConfirmed,
    confirmDeposit,
    
    // Reports
    submitReport,
    hasReported,
    
    // Info
    getDepositInfo,
    getDepositTable,
    
    // Real-time
    startBalancePolling,
};

export default BalanceService;